import type { TokenCredential } from "@azure/core-auth";
import { mintInstallationToken } from "./mint.js";

const ARM_SCOPE = "https://management.azure.com/.default";
const FOUNDRY_API = "2025-04-01-preview";

// Per-connection record of the last installation token this process
// successfully PATCHed into it, keyed by `${projectArmId}/connections/${connectionName}`.
// Separate from mintInstallationToken's (installationId, repository) token
// cache: several callers share that cache without ever touching a
// connection, so a mint-cache hit says nothing about connection state.
const connectionPatchState = new Map<string, string>();

function connectionStateKey(projectArmId: string, connectionName: string): string {
  return `${projectArmId}/connections/${connectionName}`;
}

/** TEST-ONLY: clear per-connection PATCH tracking between tests. */
export function _resetConnectionPatchState(): void {
  connectionPatchState.clear();
}

/**
 * Mint a fresh installation token + PATCH the Foundry CustomKeys project
 * connection so the worker invoke that follows finds a current Authorization
 * value in `properties.credentials.keys.Authorization`.
 *
 * The attachment shape is preserved verbatim — Foundry's MCP attachment
 * (tools[type:mcp] with project_connection_id) reads the connection's keys
 * at invoke time; we just keep refreshing them under the same connection
 * name. credentialRef → connection name.
 *
 * mintInstallationToken's cache is keyed by (installationId, repository)
 * and shared with callers that never PATCH a connection, so a cache hit
 * there does not imply this connection is current. The PATCH is skipped
 * only when this process has already pushed this exact token to this exact
 * connection (tracked in `connectionPatchState`); a new token, a new
 * connection, or a process restart all force a fresh PATCH.
 *
 * @throws on any non-2xx from GitHub or Foundry (error message carries
 *         status + body excerpt for operator diagnosis).
 */
export async function rotateConnectionAuth(args: {
  credential: TokenCredential;
  kvUri: string;
  projectArmId: string; // /subscriptions/.../projects/<proj>
  connectionName: string;
  installationId: string;
  repository: string;
}): Promise<{ rotatedAt: Date; expiresAt: Date }> {
  const minted = await mintInstallationToken({
    credential: args.credential,
    kvUri: args.kvUri,
    installationId: args.installationId,
    repository: args.repository,
  });

  const stateKey = connectionStateKey(args.projectArmId, args.connectionName);
  if (connectionPatchState.get(stateKey) !== minted.token) {
    const armTokenResp = await args.credential.getToken(ARM_SCOPE);
    if (!armTokenResp?.token) {
      throw new Error("rotateConnectionAuth: failed to acquire ARM management token");
    }
    const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_API}`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${armTokenResp.token}`,
        "Content-Type": "application/json",
      },
      // Foundry's connection resource is polymorphic on authType; the PATCH
      // deserializes as a full shape (NOT a shallow merge), so omitting the
      // discriminator returns 400 "Missing discriminator property [AuthType]".
      // Mirror the same auth shape the initial PUT in brain-link.ts uses.
      body: JSON.stringify({
        properties: {
          authType: "CustomKeys",
          category: "CustomKeys",
          credentials: { keys: { Authorization: `Bearer ${minted.token}` } },
        },
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(
        `rotateConnectionAuth: HTTP ${String(res.status)} on PATCH ${url}\n${text.slice(0, 500)}`,
      );
    }
    connectionPatchState.set(stateKey, minted.token);
  }

  return { rotatedAt: new Date(), expiresAt: minted.expiresAt };
}
